// src/components/kyc/ApplicantEntityShareholdersStep.tsx
import React, { useEffect, useMemo, useState } from "react";
import GlobalTextInputField from "../../components/GlobalTextInputField";
import UploadTableSingle from "./UploadTableSingle";
import Info from "../../assets/info.svg";
import type { Shareholder, Step4State } from "../../types/types";
import UploadTable from "./UploadTable";

type Props = {
    value: Step4State;
    onChange: (patch: Partial<Step4State>) => void;
    readOnly?: boolean;
    isAdmin?: boolean;
};

const Label: React.FC<React.PropsWithChildren> = ({ children }) => (
    <label className="text-[10px] font-semibold text-text-base">{children}</label>
);

const emptyShareholder: Shareholder = {
    fullName: "",
    percentageOwnership: "",
    dateOfBirth: "",
    nationality: "",
    passportNo: "",
    address: "",
    isPep: "",
    addressProofUrl: "",
    passportCopyUrl: "",
    additionalUrls: []
};

// 1 -> 1st, 2 -> 2nd, etc.
const ordinal = (n: number) => {
    const v = n % 100;
    if (v >= 11 && v <= 13) return `${n}th`;
    switch (n % 10) {
        case 1: return `${n}st`;
        case 2: return `${n}nd`;
        case 3: return `${n}rd`;
        default: return `${n}th`;
    }
};

const ApplicantEntityShareholdersStep: React.FC<Props> = ({
    value,
    onChange,
    readOnly,
    isAdmin
}) => {
    const holders = useMemo(
        () => (value.shareholders?.length ? value.shareholders : [{ ...emptyShareholder }]),
        [value.shareholders]
    );

    // ensure at least one record present in parent state
    useEffect(() => {
        if (!value.shareholders?.length) onChange({ shareholders: holders });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [active, setActive] = useState(0);

    // keep active index valid when list size changes
    useEffect(() => {
        if (active > holders.length - 1) setActive(Math.max(0, holders.length - 1));
    }, [holders.length, active]);

    const setHolders = (next: Shareholder[]) => onChange({ shareholders: next });

    const updateHolder = (idx: number, patch: Partial<Shareholder>) => {
        const next = holders.map((h, i) => (i === idx ? { ...h, ...patch } : h));
        setHolders(next);
    };

    const addHolder = () => {
        setHolders([...holders, { ...emptyShareholder }]);
        setActive(holders.length);
    };

    const removeHolder = (idx: number) => {
        if (holders.length === 1) {
            setHolders([{ ...emptyShareholder }]);
            setActive(0);
            return;
        }
        const next = holders.filter((_, i) => i !== idx);
        setHolders(next);
        if (active >= next.length) setActive(next.length - 1);
    };

    const h = holders[active];

    return (
        <div className="space-y-4">
            <p className="text-[10px] text-text-base">
                Provide details of all natural persons who directly own or control 20%
                or more of the entity’s shares or voting rights. Include identity
                documents and proof of address for each shareholder. If a shareholder is
                a politically exposed person (PEP), this must be disclosed.
            </p>

            {/* Pills header */}
            <div className="flex items-center gap-3 overflow-x-auto">
                {holders.map((_, i) => {
                    const isActive = i === active;
                    return (
                        <button
                            key={i}
                            type="button"
                            onClick={() => setActive(i)}
                            className={`rounded-md px-3 py-1.5 text-[14px] font-semibold whitespace-nowrap ${isActive
                                ? "bg-[#E6F0FA] text-text-base"
                                : "bg-transparent text-text-base/80 hover:bg-black/5"
                                }`}
                            disabled={readOnly}
                        >
                            {ordinal(i + 1)}
                        </button>
                    );
                })}
                {!readOnly && (
                    <button
                        type="button"
                        onClick={addHolder}
                        className="text-[14px] font-semibold whitespace-nowrap hover:underline"
                    >
                        + Add a new Shareholder
                    </button>
                )}
            </div>

            {/* Active shareholder card */}
            <div className="rounded-lg border border-border-subtle p-4 space-y-4">
                <div className="flex items-center justify-between">
                    <h3 className="text-[14px] font-semibold">
                        Shareholder {ordinal(active + 1)}
                    </h3>
                    {!readOnly && (
                        <button
                            type="button"
                            onClick={() => removeHolder(active)}
                            className="text-[12px] underline disabled:opacity-50"
                            disabled={holders.length === 1}
                        >
                            Remove
                        </button>
                    )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <GlobalTextInputField
                        title="FULL NAME"
                        value={h.fullName}
                        onChange={(e: any) =>
                            updateHolder(active, { fullName: e.target.value })
                        }
                        placeholder="Enter full name"
                        showBorder
                        disabled={readOnly}
                    />

                    <GlobalTextInputField
                        title="PERCENTAGE OF OWNERSHIP"
                        type="number"
                        value={h.percentageOwnership}
                        onChange={(e: any) => {
                            const v = e.target.value;
                            if (Number(v) < 0) return;
                            updateHolder(active, { percentageOwnership: v });
                        }}
                        placeholder="e.g. 25"
                        showBorder
                        disabled={readOnly}
                    />

                    <GlobalTextInputField
                        title="DATE OF BIRTH"
                        type="date"
                        value={h.dateOfBirth}
                        onChange={(e: any) =>
                            updateHolder(active, { dateOfBirth: e.target.value })
                        }
                        showBorder
                        disabled={readOnly}
                    />

                    <GlobalTextInputField
                        title="NATIONALITY"
                        value={h.nationality}
                        onChange={(e: any) =>
                            updateHolder(active, { nationality: e.target.value })
                        }
                        placeholder="Enter nationality"
                        showBorder
                        disabled={readOnly}
                    />

                    <GlobalTextInputField
                        title="PASSPORT NO."
                        value={h.passportNo}
                        onChange={(e: any) =>
                            updateHolder(active, { passportNo: e.target.value })
                        }
                        placeholder="Enter passport number"
                        showBorder
                        disabled={readOnly}
                    />
                    {/* PEP */}
                    <div className="flex flex-col gap-1 text-[10px]">
                        <Label>IS POLITICALLY EXPOSED (PEP)</Label>
                        <div className="relative">
                            <select
                                className="appearance-none p-3 pr-10 rounded-md border border-border-subtle w-full"
                                value={h.isPep}
                                onChange={(e) =>
                                    updateHolder(active, { isPep: e.target.value as any })
                                }
                                disabled={readOnly}
                            >
                                <option value="">Select</option>
                                <option value="yes">Yes</option>
                                <option value="no">No</option>
                            </select>
                            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                                ▾
                            </span>
                        </div>
                    </div>

                    <div className="col-span-2">
                        <GlobalTextInputField
                            title="ADDRESS"
                            value={h.address}
                            onChange={(e: any) =>
                                updateHolder(active, { address: e.target.value })
                            }
                            placeholder="Enter address"
                            showBorder
                            disabled={readOnly}
                        />
                    </div>

                    {/* PEP */}





                    {/* ADDRESS PROOF */}
                    <div className="col-span-2 grid grid-cols-2 gap-4">
                        <div className="flex flex-col gap-1 text-[10px]">
                            <Label>ADDRESS PROOF</Label>
                            <div className="flex items-center gap-1">
                                <div className="bg-bg-button-secondary p-[1px] rounded-full">
                                    <img src={Info} alt="" className="w-3 h-3" />
                                </div>
                                <span className="text-[8px]">
                                    Utility bill or lease deed dated in the last 3 months
                                </span>
                            </div>
                            <UploadTableSingle
                                value={h.addressProofUrl}
                                onChange={(url) =>
                                    updateHolder(active, { addressProofUrl: url })
                                }
                                readOnly={readOnly}
                            />
                        </div>

                        {/* PASSPORT COPY */}
                        <div className="flex flex-col gap-1 text-[10px]">
                            <Label>PASSPORT COPY</Label>
                            <div className="flex items-center gap-1">
                                <div className="bg-bg-button-secondary p-[1px] rounded-full">
                                    <img src={Info} alt="" className="w-3 h-3" />
                                </div>
                                <span className="text-[8px]">
                                    Upload Passport copy
                                </span>
                            </div>
                            <UploadTableSingle
                                value={h.passportCopyUrl}
                                onChange={(url) =>
                                    updateHolder(active, { passportCopyUrl: url })
                                }
                                readOnly={readOnly}
                            />
                        </div>
                    </div>
                    {/* ADDITIONAL DOCUMENTS */}
                    <div className="flex flex-col gap-1 text-[10px]">
                        <Label>ADDITIONAL DOCUMENTS</Label>
                        <div className="flex items-center gap-1">
                            <div className="bg-bg-button-secondary p-[1px] rounded-full">
                                <img src={Info} alt="" className="w-3 h-3" />
                            </div>
                            <span className="text-[8px]">
                                Upload any additional documents relevant to your application
                            </span>
                        </div>
                        <UploadTable
                            values={h.additionalUrls ?? []}
                            onChange={(i, url) =>
                                updateHolder(active, {
                                    additionalUrls: (h.additionalUrls ?? []).map((u, idx) =>
                                        idx === i ? url : u
                                    ),
                                })
                            }
                            add={(url) =>
                                updateHolder(active, {
                                    additionalUrls: [...(h.additionalUrls ?? []), url],
                                })
                            }
                            remove={(i) =>
                                updateHolder(active, {
                                    additionalUrls: (h.additionalUrls ?? []).filter((_, idx) => idx !== i),
                                })
                            }
                            readOnly={readOnly}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ApplicantEntityShareholdersStep;
